import { useEffect, useState } from "react";
import { ClipboardCheck, Save, UserCheck, UserX, Loader2, Calendar } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { STAGES, STAGE_KEYS, GRADES_BY_STAGE, sectionsForGrade, CURRENT_ACADEMIC_YEAR } from "../lib/constants";
import { attendanceStudents, getAttendance, saveAttendance } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function Attendance() {
  const { user } = useAuth();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [stage, setStage] = useState("");
  const [grade, setGrade] = useState("");
  const [section, setSection] = useState("");
  const [students, setStudents] = useState([]);
  const [statuses, setStatuses] = useState({}); // {student_id: "present"|"absent"}
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Restrict stage options if user has assigned stages
  const allowedStages = user?.role === "admin" || !user?.assigned_stages?.length
    ? STAGE_KEYS
    : STAGE_KEYS.filter(s => user.assigned_stages.includes(s));

  const grades = stage ? GRADES_BY_STAGE[stage] : [];
  const sections = sectionsForGrade(grade);

  const canLoad = stage && grade && section && date;

  const loadStudents = async () => {
    if (!canLoad) return;
    setLoading(true);
    try {
      const [list, existing] = await Promise.all([
        attendanceStudents(stage, grade, section),
        getAttendance(date, stage, grade, section),
      ]);
      setStudents(list);
      // Initialize all as present
      const initial = {};
      list.forEach(s => { initial[s.id] = "present"; });
      // Apply existing records
      (existing.records || []).forEach(r => {
        if (initial[r.student_id] !== undefined) initial[r.student_id] = r.status;
      });
      setStatuses(initial);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر التحميل");
    } finally {
      setLoading(false);
    }
  };

  // Auto-load when filters change
  useEffect(() => {
    if (canLoad) loadStudents();
    else setStudents([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, stage, grade, section]);

  const toggle = (id) => {
    setStatuses(prev => ({ ...prev, [id]: prev[id] === "absent" ? "present" : "absent" }));
  };

  const markAllPresent = () => {
    const all = {};
    students.forEach(s => { all[s.id] = "present"; });
    setStatuses(all);
    toast.info("تم تعليم الجميع كحاضرين");
  };

  const onSave = async () => {
    if (!canLoad || students.length === 0) {
      toast.error("اختر التاريخ والمرحلة والصف والفصل أولاً");
      return;
    }
    setSaving(true);
    try {
      const records = students.map(s => ({
        student_id: s.id,
        name: s.name,
        national_id: s.national_id || "",
        status: statuses[s.id] || "present",
      }));
      await saveAttendance({
        date, stage, grade, section,
        academic_year: CURRENT_ACADEMIC_YEAR,
        records,
      });
      toast.success("تم حفظ سجل الغياب");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر الحفظ");
    } finally {
      setSaving(false);
    }
  };

  const presentCount = Object.values(statuses).filter(v => v === "present").length;
  const absentCount = Object.values(statuses).filter(v => v === "absent").length;

  return (
    <div>
      <PageHeader title="تسجيل الغياب اليومي" subtitle="الحالة الافتراضية: حاضر — انقر زر الطالب لتغييره إلى غائب" icon={ClipboardCheck} />

      {/* Filters */}
      <Card className="p-4 sm:p-5 border-slate-200 mb-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5"><Calendar size={13} /> التاريخ</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-11" data-testid="att-date" />
          </div>
          <div className="space-y-1.5">
            <Label>المرحلة</Label>
            <Select value={stage} onValueChange={(v) => { setStage(v); setGrade(""); setSection(""); }}>
              <SelectTrigger className="h-11" dir="rtl" data-testid="att-stage"><SelectValue placeholder="اختر المرحلة" /></SelectTrigger>
              <SelectContent dir="rtl">{allowedStages.map(k => <SelectItem key={k} value={k}>{STAGES[k]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>الصف</Label>
            <Select value={grade} onValueChange={(v) => { setGrade(v); setSection(""); }} disabled={!stage}>
              <SelectTrigger className="h-11" dir="rtl" data-testid="att-grade"><SelectValue placeholder={stage ? "اختر الصف" : "اختر المرحلة أولاً"} /></SelectTrigger>
              <SelectContent dir="rtl">{grades.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>الفصل</Label>
            <Select value={section} onValueChange={setSection} disabled={!grade}>
              <SelectTrigger className="h-11" dir="rtl" data-testid="att-section"><SelectValue placeholder={grade ? "اختر الفصل" : "اختر الصف أولاً"} /></SelectTrigger>
              <SelectContent dir="rtl">{sections.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Stats + Actions */}
      {canLoad && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-5">
          <Card className="p-4 border-teal-200 bg-teal-50/40">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-teal-700 text-white flex items-center justify-center"><UserCheck size={18} /></div>
              <div><div className="text-xs text-slate-500">حاضرون</div><div className="text-2xl font-extrabold text-teal-700 tabular-nums">{presentCount}</div></div>
            </div>
          </Card>
          <Card className="p-4 border-rose-200 bg-rose-50/40">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-rose-600 text-white flex items-center justify-center"><UserX size={18} /></div>
              <div><div className="text-xs text-slate-500">غائبون</div><div className="text-2xl font-extrabold text-rose-700 tabular-nums">{absentCount}</div></div>
            </div>
          </Card>
          <Card className="p-4 border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-700 text-white flex items-center justify-center"><ClipboardCheck size={18} /></div>
              <div><div className="text-xs text-slate-500">إجمالي الطلاب</div><div className="text-2xl font-extrabold text-slate-800 tabular-nums">{students.length}</div></div>
            </div>
          </Card>
          <Card className="p-4 border-slate-200 flex items-center">
            <Button variant="outline" onClick={markAllPresent} className="w-full h-11 text-teal-700 border-teal-300" data-testid="btn-all-present">
              تعيين الجميع حاضرين
            </Button>
          </Card>
        </div>
      )}

      {/* Students list */}
      {canLoad && (
        <Card className="border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-400"><Loader2 className="animate-spin inline mx-2" size={20} /> جاري تحميل الطلاب...</div>
          ) : students.length === 0 ? (
            <div className="p-12 text-center text-slate-400">لا يوجد طلاب مسجلون في هذا الفصل</div>
          ) : (
            <div data-testid="att-list">
              {students.map((s, idx) => {
                const isAbsent = statuses[s.id] === "absent";
                return (
                  <div key={s.id} className={`flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 transition-colors ${isAbsent ? "bg-rose-50/60" : "hover:bg-teal-50/30"}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-slate-400 text-xs tabular-nums w-6 text-center">{idx + 1}</span>
                      <div className="min-w-0">
                        <div className="font-medium text-slate-800 truncate" data-testid={`att-student-${s.id}`}>{s.name}</div>
                        {s.national_id && <div className="text-xs text-slate-500 tabular-nums" dir="ltr">{s.national_id}</div>}
                      </div>
                    </div>
                    <button
                      onClick={() => toggle(s.id)}
                      className={`shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-cairo font-bold transition-all ${
                        isAbsent ? "bg-rose-600 text-white shadow-md shadow-rose-600/30" : "bg-teal-100 text-teal-800 hover:bg-teal-200"
                      }`}
                      data-testid={`att-toggle-${s.id}`}
                    >
                      {isAbsent ? <><UserX size={15} /> غائب</> : <><UserCheck size={15} /> حاضر</>}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {students.length > 0 && (
            <div className="p-4 bg-slate-50/60 border-t border-slate-200 flex justify-end">
              <Button onClick={onSave} disabled={saving} className="h-11 bg-teal-700 hover:bg-teal-800 text-white gap-2 btn-elevate px-6" data-testid="btn-save-attendance">
                {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                {saving ? "جاري الحفظ..." : "حفظ سجل الغياب"}
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
